import { useEffect, useState } from "react";
import { Phone, ArrowRight } from "lucide-react";
import { client, BOOKING_URL, NAP } from "../lib/config";
import SEOHead, { useSiteBase, roomsCollectionSchema, breadcrumbSchema } from "../lib/SEOHead";
import RoomCard from "../components/RoomCard";
import RoomDialog from "../components/RoomDialog";

export default function RoomsPage() {
  const [rooms, setRooms] = useState([]);
  const [openRoom, setOpenRoom] = useState(null);
  const { base } = useSiteBase();

  useEffect(() => {
    client.get("/rooms").then((r) => setRooms(r.data)).catch(() => {});
  }, []);

  const schema = rooms.length > 0
    ? [roomsCollectionSchema(base, rooms), breadcrumbSchema(base, [{ label: "Rooms", to: "/rooms" }])]
    : [breadcrumbSchema(base, [{ label: "Rooms", to: "/rooms" }])];

  return (
    <>
      <SEOHead
        title="Rooms & Suites in Clinton, MO"
        description="Six room types at Westbridge Inn & Suites in Clinton, MO — Jacuzzi Suite, King Suite, Three-Bedroom Family Suite, Queen Double, Standard Queen, and Accessible Queen."
        canonicalPath="/rooms"
        schema={schema}
      />

      <section className="relative bg-[#1A1A1A] text-white" data-hero-dark="true" data-testid="rooms-hero">
        <div className="absolute inset-0" aria-hidden="true">
          <img
            src="/images/room-king-1.jpg"
            alt=""
            aria-hidden="true"
            className="w-full h-full object-cover opacity-40"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/50 to-black/30" />
        </div>
        <div className="relative wb-container py-24 md:py-32">
          <div className="wb-eyebrow text-[#FF9666]">Rooms & Suites</div>
          <h1 className="wb-h1 text-white mt-4 max-w-3xl">
            Six kinds of clean room. All 32 of them freshly renovated.
          </h1>
          <p className="wb-body text-white/85 mt-6 max-w-2xl">
            Every room includes a Euro-top or comparable mattress, a 50" LED TV with cable and HBO,
            free Wi-Fi, a fridge, coffee maker, and an LED-lit granite bath. Prices are per night and
            per room. Kids under 18 stay free in existing beds.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <a
              href={BOOKING_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="wb-btn-primary"
              data-testid="rooms-hero-book"
            >
              Check availability <ArrowRight className="h-4 w-4" />
            </a>
            <a
              href={`tel:${NAP.phoneTel}`}
              className="inline-flex items-center gap-2 bg-white/95 hover:bg-white text-[#1A1A1A] font-bold py-3 px-6 rounded-sm"
              style={{ fontFamily: "'Nunito', sans-serif" }}
              data-testid="rooms-hero-call"
            >
              <Phone className="h-4 w-4 text-[#B94A15]" /> {NAP.phone}
            </a>
          </div>
        </div>
      </section>

      <section className="bg-[#FDFBF7]" data-testid="rooms-grid-section">
        <div className="wb-container py-16 md:py-24">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {rooms.map((room) => (
              <RoomCard key={room.slug} room={room} onOpen={setOpenRoom} />
            ))}
          </div>
        </div>
      </section>

      <section className="bg-[#F5F0E6] border-y border-[#E5DFD3]">
        <div className="wb-container py-16 md:py-24 grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
          <div>
            <div className="wb-eyebrow">Not sure which room fits?</div>
            <h2 className="wb-h2 mt-4">Call the desk. A real person will talk you through it.</h2>
            <p className="wb-body mt-4">
              We've been booking rooms for families, work crews, wedding parties, hunters, and one
              retired trucker who's been coming back for 8 years. Tell us who's on the trip — we'll
              tell you which room actually fits.
            </p>
          </div>
          <div className="flex flex-wrap gap-3 lg:justify-end">
            <a
              href={`tel:${NAP.phoneTel}`}
              className="wb-btn-secondary"
              data-testid="rooms-cta-call"
            >
              <Phone className="h-4 w-4" /> {NAP.phone}
            </a>
            <a
              href={BOOKING_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="wb-btn-primary"
              data-testid="rooms-cta-book"
            >
              Book Now <ArrowRight className="h-4 w-4" />
            </a>
          </div>
        </div>
      </section>

      <RoomDialog room={openRoom} open={!!openRoom} onOpenChange={(v) => !v && setOpenRoom(null)} />
    </>
  );
}
